import styled from '@emotion/styled';
import type { TrackerTag } from '@racemap/sdk/schema/trackerTags';
import type { TrackerObject } from '@racemap/utilities/types/trackers';
import { ObjectId } from '@racemap/utilities/types/utils';
import { Flex, Tooltip } from 'antd';
import type { Immutable } from 'immer';
import type { TableCellProps } from 'react-virtualized';
import { TrackerColumnKeys, type TrackerTags } from 'src/store/trackers/trackers_reducers';
import { Columns } from '../../../DataTable';
import { IconTag, IconTrash } from '../../../Icon';

interface CellProps extends TableCellProps {
  rowData: TrackerObject;
}

export const TagsColumn = ({
  trackerTags,
  updateTrackerTag,
}: {
  trackerTags: Immutable<TrackerTags>;
  updateTrackerTag: (tagId: ObjectId, trackerTagUpdate: Partial<TrackerTag>) => Promise<void>;
}): JSX.Element => {
  const handleDelete = async (tagId: string, trackerId: string) => {
    const tag = trackerTags.get(tagId);
    if (tag) {
      await updateTrackerTag(new ObjectId(tagId), {
        trackerIds: tag.trackerIds.filter((id) => id.toString() !== trackerId),
      });
    }
  };

  const cellRenderer = ({ rowData }: CellProps): JSX.Element | null => {
    const { id } = rowData;
    const tagElements = Array.from(trackerTags.entries())
      .filter(([_, value]) =>
        value.trackerIds.some((trackerId) => trackerId.toHexString() === id.toHexString()),
      )
      .map(([key, value]) => (
        <Tooltip
          title={
            <>
              <span>{value.label}</span>
              <TrashButton onClick={() => handleDelete(key, id.toHexString())} />
            </>
          }
          key={key}
          color={value.color}
        >
          <TagIcon color={value.color} />
        </Tooltip>
      ));

    return tagElements.length > 0 ? <Container>{tagElements}</Container> : null;
  };

  return Columns.basicColumn({
    dataKey: TrackerColumnKeys.TAGS,
    label: 'Tags',
    cellRenderer,
    minWidth: 80,
    style: { justifyContent: 'left', paddingLeft: '15px' },
    headerStyle: { justifyContent: 'center' },
    columnData: { hiddenOnDefault: true },
    hint: 'Tags are used to categorize trackers. Hover over a tag to see its details and remove it from the tracker.',
  });
};

const Container = styled(Flex)`
  overflow-x: auto;
 scrollbar-width: thin;
`;

const TrashButton = styled(IconTrash)`
  cursor: pointer;
  padding-left: 5px;
`;

const TagIcon = styled(IconTag)`
  padding-left: 2px;
  padding-right: 2px;
`;
